import { forwardRef } from 'react';
import {
  ListItemButton as MuiListItemButton,
  ListItemButtonProps as MuiListItemButtonProps,
} from '@mui/material';

/**
 * ListItemButton component - Clickable list item (preferred over ListItem with button prop).
 *
 * This is a thin wrapper around Material-UI's ListItemButton component that provides:
 * - Interactive clickable list items
 * - Button semantics and accessibility
 * - Selected state styling
 * - Disabled state support
 * - Ripple effects and hover states
 * - Theme integration
 *
 * Note: This component is preferred over using `<ListItem button>` which is deprecated.
 * ListItemButton provides better semantics and accessibility for interactive list items.
 *
 * Common use cases:
 * - Navigation menu items
 * - Selectable list items
 * - Interactive settings options
 * - Clickable work items
 *
 * @example
 * // Clickable list item
 * <ListItemButton onClick={handleClick}>
 *   <ListItemText primary="Click me" />
 * </ListItemButton>
 *
 * @example
 * // Selected state
 * <ListItemButton selected={isSelected}>
 *   <ListItemText primary="Selected item" />
 * </ListItemButton>
 *
 * @example
 * // Disabled state
 * <ListItemButton disabled>
 *   <ListItemText primary="Disabled item" />
 * </ListItemButton>
 */
export type ListItemButtonProps = MuiListItemButtonProps;

export const ListItemButton = forwardRef<HTMLDivElement, ListItemButtonProps>((props, ref) => (
  <MuiListItemButton ref={ref} {...props} />
));

ListItemButton.displayName = 'ListItemButton';
